import { areaEntity, areaHasSavedViews, listPathFor, pageForRoute, viewPathFor, visibleAreas, type AreaId } from '@/navigation/areas';
import type { Gate } from '@/navigation/gates';
import { parseViewParam } from '@/composables/routedSavedView';
import type { SavedViewRecord } from '@/utils/savedViews';

/**
 * What one area offers in the header: its record types, its pages and your
 * saved views there, the same items as the sidebar. The desktop menu, the
 * phone's pill row and the record page's menu all read this, so they cannot
 * disagree about what is there or which item is current.
 */

/** Where you are, for example the Buckets list with view 7 open. */
export interface PickerLocation {
    routeName: string;
    path: string;
    entityId: string | null;
    viewId: number | null;
}

export interface PickerRecordItem {
    key: string;
    label: string;
    entityId: string;
    to: string;
    current: boolean;
}

export interface PickerPageItem {
    key: string;
    label: string;
    routeName: string;
    to: string;
    current: boolean;
}

export interface PickerViewItem {
    key: string;
    label: string;
    view: SavedViewRecord;
    to: { path: string; query: { view: string } };
    current: boolean;
}

export interface AreaPicker {
    recordItems: PickerRecordItem[];
    pageItems: PickerPageItem[];
    viewItems: PickerViewItem[];
    /** The open view, only when it is among the loaded views. A deleted view is null. */
    matchedView: SavedViewRecord | null;
    /** The open view's name, else the list's name, else the page's name. Empty on Home. See pickerLabel. */
    label: string;
    /** True when at least one item is not the one you are on, so there is somewhere else to go. */
    hasChoice: boolean;
    /** True when the area has saved views, so a phone shows its Saved views and Shared views pills. */
    hasSavedViews: boolean;
}

export function areaPickerItems(input: {
    areaId: AreaId;
    gateIsOpen: (gate: Gate) => boolean;
    views: SavedViewRecord[];
    location: PickerLocation;
}): AreaPicker {
    const { areaId, gateIsOpen, views, location } = input;
    const area = visibleAreas(gateIsOpen).find((candidate) => candidate.id === areaId);
    const matchedView = location.viewId === null ? null : (views.find((view) => view.id === location.viewId) ?? null);

    const recordItems: PickerRecordItem[] = (area?.entities ?? []).map((entity) => ({
        key: entity.entityId,
        label: entity.label,
        entityId: entity.entityId,
        to: listPathFor(entity.entityId),
        current: matchedView === null && location.entityId === entity.entityId,
    }));
    const pageHere = pageForRoute(location.routeName, location.path);
    const pageItems: PickerPageItem[] = (area?.pages ?? []).map((page) => ({
        key: page.routeName,
        label: page.label,
        routeName: page.routeName,
        to: page.path,
        current: matchedView === null && pageHere?.routeName === page.routeName,
    }));
    const viewItems: PickerViewItem[] = views.flatMap((view) => {
        const to = viewPathFor(view.entity, view.id);
        return to === null ? [] : [{ key: `view-${view.id}`, label: view.name, view, to, current: matchedView?.id === view.id }];
    });

    const allItems = [...recordItems, ...pageItems, ...viewItems];

    return {
        recordItems,
        pageItems,
        viewItems,
        matchedView,
        label: pickerLabel(matchedView, location, pageItems),
        hasChoice: allItems.some((item) => !item.current),
        hasSavedViews: area !== undefined && areaHasSavedViews(area),
    };
}

/**
 * The open view's name, else the list's name, else the page's name. On a
 * list whose record type is unknown, the label is empty. It never falls back
 * to a page's name there.
 */
function pickerLabel(matchedView: SavedViewRecord | null, location: PickerLocation, pageItems: PickerPageItem[]): string {
    if (matchedView) {
        return matchedView.name;
    }
    if (location.entityId) {
        return areaEntity(location.entityId)?.label ?? '';
    }
    return pageItems.find((item) => item.current)?.label ?? '';
}

/**
 * A list address split into its path and the saved view it opens, for
 * example "/players?view=7" into "/players" and 7. The record page keeps the
 * address of the list it came from, and reads it with this.
 */
export function splitListPath(listPath: string): { path: string; viewId: number | null } {
    const [addressWithoutHash] = listPath.split('#', 1);
    const [path, queryString = ''] = addressWithoutHash.split('?', 2);
    return { path, viewId: parseViewParam(new URLSearchParams(queryString).get('view') ?? undefined) };
}
